import { getImageUrl } from './helpers/images';
import { getClientTheme, THEME_CSS_PROPERTIES } from './helpers/themes';
import { applyFonts, loadTenantFonts } from './helpers/fonts';

// Reads ?tenant=&type=&id= and, if present, builds the same embed
// markup clients use and swaps out the static demo content on the page.
(function () {
  let TENANT_SUBDOMAIN = process.env.TENANT_SUBDOMAIN;
  let TENANT_BASE_DOMAIN = '.playbook.pulselive.com';
  let BACKGROUND_IMAGE_WIDTH = 2560;
  let LOGO_IMAGE_HEIGHT = 96;

  const jsFilePath = '/latest/client.min.js';
  const SDK_SUBDOMAIN = 'sdk';
  // Maps a "type" query param to the SDK bundle that renders it

  let SDK_BUNDLES = {
    poll: {
      sdkBundle: `playbook-sdk-client-web`,
      activationAttr: 'data-fek-poll',
    },
    quiz: {
      sdkBundle: 'playbook-quizzes-sdk-web',
      activationAttr: 'data-fek-quiz',
    },
    'tournament-predictor': {
      sdkBundle: 'playbook-tournament-predictor-sdk-web',
      activationAttr: 'data-playbook-tournament-predictor',
    },
    trivia: {
      sdkBundle: 'playbook-sdk-client-web',
      activationAttr: 'data-fek-trivia',
    },
    'data-capture': {
      sdkBundle: 'playbook-sdk-client-web',
      activationAttr: 'data-fek-form',
    },
    predictions: {
      sdkBundle: 'playbook-sdk-client-web',
      activationAttr: 'data-fek-prediction',
    },
    potg: {
      sdkBundle: 'playbook-potg-sdk-web',
      activationAttr: 'data-fek-potg',
    },
    'score-predictor': {
      sdkBundle: 'playbook-score-predictor-sdk-web',
      activationAttr: 'data-fek-score-predictor',
    },
    'team-selector': {
      sdkBundle: 'playbook-team-selector-sdk-web',
      activationAttr: 'data-fek-team-selector',
    },
    hol: {
      sdkBundle: 'playbook-higher-lower-sdk-web',
      activationAttr: 'data-pb-higher-lower',
    },
    'grid-guesser': {
      sdkBundle: 'playbook-grid-guesser-sdk-web',
      activationAttr: 'data-pb-gridguesser',
    },
  };

  let params = new URLSearchParams(window.location.search);
  let tenant = params.get('tenant');
  let type = params.get('type');
  let activationId = params.get('id');
  let clientThemeEnabled = false;
  let footerTextColour = null;
  // Configured heading/body fonts, set from tenant settings
  let fonts = {};
  let sdkBundle = SDK_BUNDLES[type];

  const hasThemeAvailable = Boolean(getClientTheme(tenant));

  const acceptedTypes = ['poll', 'trivia', 'quiz', 'predictor'];
  const dynamicTypeName = acceptedTypes.includes(type) ? type : 'experience';
  // Custom page tab title
  const dynamicPageTitle = `${formatString(tenant)} | ${formatString(type)}`;
  document.title = dynamicPageTitle;
  // Capitalise first letter of each space-separated word
  function capitalizeFirstLetter(val) {
    return String(val)
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  // Replace hyphens weith spaces, then run capitalizeFirstLetter function
  function formatString(string) {
    return capitalizeFirstLetter((string || '').replace(/-/g, ' '));
  }

  if (!tenant || !type || !activationId || !sdkBundle) {
    renderActivationNotFound();
    return;
  }

  let attributeName = sdkBundle.activationAttr;
  let bundleName = sdkBundle.sdkBundle;

  let SDK_SCRIPT_URL = `https://${SDK_SUBDOMAIN}${TENANT_SUBDOMAIN}${TENANT_BASE_DOMAIN}/${bundleName}${jsFilePath}`;

  // Set tenant on window.PLAYBOOK_CONFIG for getImageUrl helper, and the
  // environment subdomain the SDKs use to build their API host
  if (!window.PLAYBOOK_CONFIG) {
    window.PLAYBOOK_CONFIG = {};
  }
  window.PLAYBOOK_CONFIG.tenant = tenant;
  window.PLAYBOOK_CONFIG.subdomain = TENANT_SUBDOMAIN;
  applyTenantSettings();

  /**
   *
   */
  function tenantHost() {
    return tenant + TENANT_SUBDOMAIN + TENANT_BASE_DOMAIN;
  }

  /**
   *
   */
  function applyTenantSettings() {
    let endpoint = `https://${tenantHost()}/api/settings/v1/fan/settings`;

    fetch(endpoint, {
      headers: { 'Content-Type': 'application/json' },
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`Request failed with status ${res.status}`);
        }
        return res.json();
      })
      .then((settings) =>
        // Before rendering, so licensed fonts resolve when applied
        loadTenantFonts(tenant, [
          settings.heading_font,
          settings.body_font,
        ]).then(() => settings),
      )
      .then((settings) => {
        renderSettings(settings);
        renderEmbed();
      })
      .catch(() => {
        renderActivationNotFound();
      });
  }

  // Render the 'Embed can't be found' message
  function renderActivationNotFound() {
    let root = document.getElementById('fek-dynamic-root');
    root.textContent = '';
    let messageWrapper = document.createElement('div');
    messageWrapper.className = 'error-message';
    let messageTitle = document.createElement('h2');
    messageTitle.textContent = `This ${dynamicTypeName} cannot be found.`;
    let messageDescriptionLines = [
      'Double-check the URL.',
      'If the URL is correct, the poll may no longer be available.',
    ];
    let messageDescriptionWrapper = document.createElement('div');
    messageDescriptionWrapper.className = 'error-message-description';
    if (footerTextColour) {
      messageWrapper.style.color = footerTextColour;
    }
    messageWrapper.appendChild(messageTitle);
    messageDescriptionLines.forEach((line) => {
      let messageDescription = document.createElement('p');
      messageDescription.textContent = line;
      messageDescriptionWrapper.appendChild(messageDescription);
    });
    messageWrapper.appendChild(messageDescriptionWrapper);
    root.appendChild(messageWrapper);
  }

  /**
   * The injected SDK script makes its own fetch calls (e.g. to load poll/quiz
   * data for the given activationId). Wrapping fetch is the only way to see
   * those responses, since they happen inside a separately-loaded script.
   */
  function watchForActivationNotFound() {
    let originalFetch = window.fetch;

    window.fetch = function (...args) {
      return originalFetch.apply(this, args).then((res) => {
        if (!res.ok) {
          renderActivationNotFound();
        }
        return res;
      });
    };
  }

  /**
   * Applies the tenant's theme as CSS custom properties on the given element.
   */
  function applyClientTheme(el) {
    if (!hasThemeAvailable || !clientThemeEnabled) {
      return;
    }

    let clientTheme = getClientTheme(tenant);
    Object.entries(THEME_CSS_PROPERTIES).forEach(([key, cssProperty]) => {
      if (clientTheme[key]) {
        el.style.setProperty(cssProperty, clientTheme[key]);
      }
    });
  }

  /**
   * Applies the theme on the document root so any element on the page
   * (error message, etc.) can use it via inheritance.
   */
  function applyClientThemeVariables() {
    applyClientTheme(document.documentElement);
  }

  /**
   *
   * @param settings
   */
  function renderSettings(settings) {
    clientThemeEnabled = Boolean(settings.client_theme_enabled);
    applyClientThemeVariables();

    fonts = {
      heading: settings.heading_font,
      body: settings.body_font,
    };
    
    // On the root too, so page elements outside the embed (footer, error
    // message) can read --font-body/--font-heading
    applyFonts(document.documentElement, fonts);


    if (settings.background_colour) {
      document.body.style.backgroundColor = settings.background_colour;
    }

    if (settings.background_image_url) {
      document.body.style.backgroundImage = `url('${getImageUrl(settings.background_image_url, BACKGROUND_IMAGE_WIDTH)}')`;
      document.body.style.backgroundPosition = 'center';
      document.body.style.backgroundSize = 'cover';
    }

    let headerEl = document.getElementById('settings-header');
    if (settings.homepage_link_image_url) {
      let link = document.createElement('a');
      if (settings.homepage_link_url) {
        link.href = settings.homepage_link_url;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
      }
      link.className = 'header-image';
      let logo = document.createElement('img');
      logo.src = getImageUrl(
        settings.homepage_link_image_url,
        undefined,
        LOGO_IMAGE_HEIGHT,
      );
      link.appendChild(logo);
      headerEl.appendChild(link);
    }

    let footerEl = document.getElementById('settings-footer');
    if (settings.footer_text) {
      footerEl.textContent = settings.footer_text;
      footerEl.style.color = settings.footer_text_colour || '#ffffff';
    }
  }

  /**
   *
   */
  function renderEmbed() {
    let embed = document.createElement('div');
    embed.id = `fek-${activationId}`;
    embed.setAttribute(attributeName, activationId);
    embed.setAttribute('data-fek-tenant', tenant);
    embed.setAttribute('data-dynamic-hosting', true);
    document.getElementById('fek-dynamic-root').appendChild(embed);

    applyClientTheme(embed);
    // After the theme, so configured fonts override any theme fonts
    applyFonts(embed, fonts);

    watchForActivationNotFound();

    let script = document.createElement('script');
    script.async = true;
    script.src = SDK_SCRIPT_URL;
    document.body.appendChild(script);
  }
})();
